/* External */
import type { Seq } from 'immutable';

/* Utils */
import { sizeByViewport } from '../../utils/cardsUtils';
import type { FocusRestorableComponent } from './withFocusRestorable.hoc';

type FocusRestorableComponentsSeq = Seq.Keyed<string, FocusRestorableComponent>;

export function filterByFocusRestorable(
  focusableComponents: FocusRestorableComponentsSeq,
): FocusRestorableComponentsSeq {
  return focusableComponents.filter(
    (component: FocusRestorableComponent): boolean => component.isFocusRestorable(),
  );
}

export function filterByFullyVisible(
  focusableComponents: FocusRestorableComponentsSeq,
): FocusRestorableComponentsSeq {
  return focusableComponents.filter(
    (component: FocusRestorableComponent): boolean => component.isFullyVisible(sizeByViewport(10)),
  );
}

export function mapByVisibleArea(
  focusableComponents: FocusRestorableComponentsSeq,
): Seq.Keyed<string, number> {
  return focusableComponents.map(
    (component: FocusRestorableComponent): number => component.getVisibleArea(),
  );
}

export function mapByTopLeftPosition(
  focusableComponents: FocusRestorableComponentsSeq,
): Seq.Keyed<string, number> {
  return focusableComponents.map(
    (component: FocusRestorableComponent): number => component.getTopLeftPosition(),
  );
}

export function restoreFocusStrategy(
  focusableComponents: FocusRestorableComponentsSeq,
  lastHoveredRealFocusKey: string,
) {
  const focusRestorableComponents = filterByFocusRestorable(focusableComponents);
  const fullyVisibleComponents = filterByFullyVisible(focusRestorableComponents);

  const lastHoveredComponent = fullyVisibleComponents.get(lastHoveredRealFocusKey);
  if (lastHoveredComponent) {
    return lastHoveredRealFocusKey;
  }

  const [minTopLeftFocusKey] = mapByTopLeftPosition(fullyVisibleComponents)
    .entrySeq().minBy(([, position]) => position) ?? [];

  if (minTopLeftFocusKey) return minTopLeftFocusKey;

  const [maxVisibleAreaFocusKey] = mapByVisibleArea(focusRestorableComponents)
    .entrySeq().maxBy(([, visibleArea]) => visibleArea) ?? [];

  return maxVisibleAreaFocusKey;
}
